import { useMemo, useRef } from "react";
import Table from "views/merchandise/tables/BulkActionsTable";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import { useAppSelector } from "redux/hooks";
import useDSPAudienceData from "./useDSPAudienceData";
import AudienceExpander from "./AudienceExpander";
import { buildTextColumn } from "views/merchandise/columns/buildTextColumn";
import { trellisPalette } from "components/custom/analytics/palettes";

const DSPAudiencesTable = ({ adGroupId, untargetedReport = false, totalImpressions = 0 }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const tableRef = useRef();
  const TABLE_ID = "dspAudiencesTable";
  const filterProps = useCustomTableFilters(TABLE_ID);

  const {
    data: dspAudienceStatsData,
    metadata: dspAudienceMetadata,
    isLoading,
  } = useDSPAudienceData("segment_id", true, { line_item_id: adGroupId });

  const dspAudienceTableData = useMemo(() => {
    return dspAudienceStatsData
      .map((row) => {
        const metadata = dspAudienceMetadata?.find((meta) => meta.segment_id === row.segment_id);
        return { ...row, ...metadata };
      })
      ?.filter((row) => {
        if (untargetedReport) {
          return true;
        } else {
          return !["untargeted"].includes(row.targeting_method);
        }
      });
  }, [dspAudienceStatsData, dspAudienceMetadata, untargetedReport]);

  const columnSpecs = useMemo(
    () => [
      buildTextColumn("targeting_method", "Targeting Method", {
        toolTip: `The audience targeting method`,
      }),
      buildTextColumn("line_item_id", "Line Item ID", {
        toolTip: `The audience target line item ID`,
        checked: false,
      }),
      { key: "dsp_sales", compareKey: "dsp_sales_percent", options: { checked: false } },
      { key: "dsp_total_sales", compareKey: "dsp_total_sales_percent", options: { checked: false } },
      {
        key: "overlap_impression_share",
        compareKey: null,
        options: {
          metric: {
            id: "overlap_impression_share",
            format: "text",
            accessor: (d) => d,
            name: "Share of Total Impressions",
            options: { width: 185, checked: untargetedReport },
          },
          formatter: (row) => {
            const audienceImpressions = row.impressions ?? 0;
            const shareDivWidth = `${(audienceImpressions / totalImpressions) * 100}%`;

            return (
              <div
                style={{
                  position: "relative",
                  width: "100px",
                  height: "12px",
                  borderRadius: "3px",
                  backgroundColor: "#EEE",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: shareDivWidth,
                    height: "12px",
                    borderRadius: "3px",
                    backgroundColor: trellisPalette[2],
                  }}
                />
              </div>
            );
          },

          hideFilter: true,
        },
      },
      { key: "dsp_ecpm", compareKey: "dsp_ecpm_percent" },
      { key: "dsp_impressions", compareKey: "dsp_impressions_percent" },
      { key: "dsp_clicks", compareKey: "dsp_clicks_percent" },
      { key: "dsp_cost", compareKey: "dsp_cost_percent" },
      { key: "dsp_total_purchases", compareKey: "dsp_total_purchases_percent" },
      { key: "dsp_units", compareKey: "dsp_units_percent" },
      { key: "dsp_total_units", compareKey: "dsp_total_units_percent", options: { checked: false } },
      { key: "dsp_ctr", compareKey: "dsp_ctr_delta" },
      {
        key: "dsp_total_acos",
        compareKey: "dsp_total_acos_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_roas",
        compareKey: "dsp_total_roas_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_detail_page_views",
        compareKey: "dsp_total_detail_page_views_percent",
        options: { checked: false },
      },
      {
        key: "dsp_total_detail_page_view_rate",
        compareKey: "dsp_total_detail_page_view_rate_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_add_to_cart",
        compareKey: "dsp_total_add_to_cart_percent",
        options: { checked: false },
      },
      {
        key: "dsp_new_to_brand_detail_page_views",
        compareKey: "dsp_new_to_brand_detail_page_views_percent",
        options: { checked: false },
      },
      {
        key: "dsp_new_to_brand_detail_page_views_rate",
        compareKey: "dsp_new_to_brand_detail_page_views_rate_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_new_to_brand_dpvs",
        compareKey: "dsp_total_new_to_brand_dpvs_percent",
        options: { checked: false },
      },
      {
        key: "dsp_total_new_to_brand_dpv_rate",
        compareKey: "dsp_total_new_to_brand_dpv_rate_delta",
        options: { checked: false },
      },
      {
        key: "dsp_brand_searches",
        compareKey: "dsp_brand_searches_percent",
        options: { checked: false },
      },
      {
        key: "dsp_brand_searches_rate",
        compareKey: "dsp_brand_searches_rate_delta",
        options: { checked: false },
      },
    ],
    [totalImpressions, untargetedReport],
  );

  return (
    <Table
      marketPlace={marketPlace}
      tableRef={tableRef}
      tableId={untargetedReport ? "dspAudiencesOverlap" : "dspAudiences"}
      isExpandable={true}
      isSelectable={false}
      titleCol={{
        id: "segment",
        isStatic: true,
        Header: "Segment",
        accessor: "text",
        format: "text",
        style: { width: "300px" },
        width: 300,
        Cell: ({ row }) => {
          const { segment_name } = row._original ?? {};

          return (
            <span
              style={{
                textDecoration: "none",
                fontWeight: "500",
                fontSize: "1.5rem",
                cursor: "pointer",
              }}
            >
              {segment_name}
            </span>
          );
        },
        Filter: () => null,
      }}
      data={dspAudienceTableData}
      columnSpecs={columnSpecs}
      getSearchCriteria={(row) => {
        const { segment_name, segment_source, targeting_method } = row ?? {};
        return `${segment_name} ${segment_source} ${targeting_method}`;
      }}
      tableRowDescription={"Audiences"}
      defaultSorted={[{ id: "dsp_impressions", desc: true }]}
      isLoading={isLoading}
      filterWidget={true}
      hideFilterToggleIcon={true}
      hideResetFiltersIcon={false}
      {...filterProps}
      handleBulkChanges={() => {}}
      minRows={1}
      selectionKey={"id"}
      ExpandedContent={({ row }) => <AudienceExpander row={row.row} />}
    />
  );
};

export default DSPAudiencesTable;
